import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import {
  LuPackage,
  LuRefreshCw,
  LuPlus,
  LuHardDrive,
  LuUsers,
  LuSmartphone,
  LuPencil,
  LuTrash2,
  LuToggleLeft,
  LuToggleRight,
  LuX
} from 'react-icons/lu';
import '../styles/AdminOptionsPage.css';

const OPTION_TYPES = [
  { value: 'traffic', label: 'Трафик', icon: '📊', unit: 'GB' },
  { value: 'devices', label: 'Устройства', icon: '📱', unit: 'шт' },
  { value: 'squad', label: 'Сквад', icon: '👥', unit: '' }
];

const AVAILABLE_ICONS = ['📊', '📱', '👥', '🚀', '💎', '⚡', '🔥', '✨', '🎯', '💫', '🌟', '📦', '🔌', '💻', '🖥️', '📶'];

export default function AdminOptionsPage() {
  const { token } = useAuth();
  const [options, setOptions] = useState([]);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [formData, setFormData] = useState({
    option_type: 'traffic',
    name: '',
    description: '',
    value: '',
    squad_uuid: '',
    unit: 'GB',
    price_uah: 0,
    price_rub: 0,
    price_usd: 0,
    is_active: true,
    sort_order: 0,
    icon: '📊'
  });

  const fetchOptions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/options', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Ошибка загрузки опций');
      const data = await res.json();
      setOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSquads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/admin/squads', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setSquads(Array.isArray(data) ? data : []);
    } catch (_) {
      // optional
    }
  }, [token]);

  useEffect(() => {
    fetchOptions();
    fetchSquads();
  }, [fetchOptions, fetchSquads]);

  const groupedOptions = useMemo(() => {
    const groups = { traffic: [], devices: [], squad: [] };
    for (const opt of options) {
      if (groups[opt.option_type]) groups[opt.option_type].push(opt);
    }
    return groups;
  }, [options]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'traffic': return <LuHardDrive />;
      case 'devices': return <LuSmartphone />;
      case 'squad': return <LuUsers />;
      default: return <LuPackage />;
    }
  };

  const openCreate = (presetType = 'traffic') => {
    const meta = OPTION_TYPES.find(t => t.value === presetType) || OPTION_TYPES[0];
    setEditingOption(null);
    setFormData({
      option_type: presetType,
      name: '',
      description: '',
      value: '',
      squad_uuid: '',
      unit: meta.unit || '',
      price_uah: 0,
      price_rub: 0,
      price_usd: 0,
      is_active: true,
      sort_order: options.length,
      icon: meta.icon || '📦'
    });
    setShowModal(true);
  };

  const openEdit = (opt) => {
    setEditingOption(opt);
    setFormData({
      option_type: opt.option_type || 'traffic',
      name: opt.name || '',
      description: opt.description || '',
      value: opt.value ?? '',
      squad_uuid: opt.squad_uuid || '',
      unit: opt.unit || '',
      price_uah: opt.price_uah || 0,
      price_rub: opt.price_rub || 0,
      price_usd: opt.price_usd || 0,
      is_active: Boolean(opt.is_active),
      sort_order: opt.sort_order || 0,
      icon: opt.icon || '📦'
    });
    setShowModal(true);
  };

  const onTypeChange = (newType) => {
    const meta = OPTION_TYPES.find(t => t.value === newType);
    setFormData(prev => ({
      ...prev,
      option_type: newType,
      unit: meta?.unit ?? prev.unit,
      icon: meta?.icon ?? prev.icon,
      squad_uuid: newType === 'squad' ? prev.squad_uuid : '',
    }));
  };

  const saveOption = async (e) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const url = editingOption ? `/api/admin/options/${editingOption.id}` : '/api/admin/options';
      const method = editingOption ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Ошибка сохранения');
      setShowModal(false);
      await fetchOptions();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteOption = async (opt) => {
    if (!token) return;
    const ok = window.confirm(`Удалить опцию "${opt.name}"?`);
    if (!ok) return;
    try {
      const res = await fetch(`/api/admin/options/${opt.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Ошибка удаления');
      await fetchOptions();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleOption = async (opt) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/admin/options/${opt.id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Ошибка переключения');
      await fetchOptions();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="admin-options-page">
      <div className="options-header">
        <div className="options-title-section">
          <h1 className="options-title">
            <LuPackage className="title-icon" />
            Дополнительные опции
          </h1>
          <p className="options-subtitle">Управление опциями для докупки: трафик, устройства, сквады</p>
        </div>

        <div className="options-actions">
          <button className="btn-refresh" onClick={fetchOptions} disabled={loading}>
            <LuRefreshCw className={loading ? 'spin' : ''} />
            Обновить
          </button>
          <button className="btn-create" onClick={() => openCreate('traffic')}>
            <LuPlus />
            Создать опцию
          </button>
        </div>
      </div>

      {error && (
        <div className="options-error">
          <div>{error}</div>
          <button onClick={() => setError(null)} aria-label="Close"><LuX /></button>
        </div>
      )}

      {loading ? (
        <div className="options-loading">
          <LuRefreshCw className="spin" /> Загрузка...
        </div>
      ) : (
        <div className="options-groups">
          {OPTION_TYPES.map((t) => (
            <div className="options-group" key={t.value}>
              <div className="group-header">
                <div className="group-title">
                  {getTypeIcon(t.value)}
                  {t.label}
                  <span className="group-count">{(groupedOptions[t.value] || []).length}</span>
                </div>
                <button className="btn-add-small" onClick={() => openCreate(t.value)} title="Добавить">
                  <LuPlus />
                </button>
              </div>

              <div className="options-list">
                {(groupedOptions[t.value] || []).length === 0 ? (
                  <div className="options-empty">Опций пока нет</div>
                ) : (
                  (groupedOptions[t.value] || []).map((opt) => (
                    <div className="option-card" key={opt.id}>
                      <div className="option-icon" title="Иконка">{opt.icon || '📦'}</div>

                      <div className="option-main">
                        <div className="option-title-row">
                          <div className="option-name">{opt.name}</div>
                          <div className="option-badge">
                            {opt.value}{opt.unit ? ` ${opt.unit}` : ''}
                          </div>
                          {!opt.is_active && (
                            <div className="option-badge" style={{ background: 'rgba(239,68,68,.12)', color: '#ef4444', borderColor: 'rgba(239,68,68,.25)' }}>
                              OFF
                            </div>
                          )}
                        </div>
                        {opt.description ? <div className="option-desc">{opt.description}</div> : null}
                        <div className="option-meta">
                          <span className="meta-chip">UAH: {Number(opt.price_uah || 0).toFixed(2)}</span>
                          <span className="meta-chip">RUB: {Number(opt.price_rub || 0).toFixed(2)}</span>
                          <span className="meta-chip">USD: {Number(opt.price_usd || 0).toFixed(2)}</span>
                          <span className="meta-chip">order: {opt.sort_order ?? 0}</span>
                          {opt.option_type === 'squad' && opt.squad_uuid ? (
                            <span className="meta-chip">squad: {opt.squad_uuid}</span>
                          ) : null}
                        </div>
                      </div>

                      <div className="option-actions">
                        <button className="btn-icon-sm" onClick={() => toggleOption(opt)} title="Активность">
                          {opt.is_active ? <LuToggleRight /> : <LuToggleLeft />}
                        </button>
                        <button className="btn-icon-sm" onClick={() => openEdit(opt)} title="Редактировать">
                          <LuPencil />
                        </button>
                        <button className="btn-icon-sm btn-danger" onClick={() => deleteOption(opt)} title="Удалить">
                          <LuTrash2 />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="options-modal-backdrop" onClick={() => !saving && setShowModal(false)}>
          <div className="options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="options-modal-header">
              <div className="options-modal-title">
                <LuPackage />
                {editingOption ? 'Редактировать опцию' : 'Создать опцию'}
              </div>
              <button className="btn-icon-sm" onClick={() => !saving && setShowModal(false)} aria-label="Close">
                <LuX />
              </button>
            </div>

            <form onSubmit={saveOption}>
              <div className="options-modal-body">
                <div className="options-form-grid">
                  <div className="options-form-field">
                    <label>Тип</label>
                    <select value={formData.option_type} onChange={(e) => onTypeChange(e.target.value)}>
                      {OPTION_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="options-form-field">
                    <label>Иконка</label>
                    <select value={formData.icon} onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}>
                      {AVAILABLE_ICONS.map(ic => (
                        <option key={ic} value={ic}>{ic}</option>
                      ))}
                    </select>
                  </div>

                  <div className="options-form-field">
                    <label>Название</label>
                    <input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
                  </div>

                  <div className="options-form-field">
                    <label>Значение</label>
                    <input value={formData.value} onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))} required />
                  </div>

                  <div className="options-form-field">
                    <label>Ед. измерения</label>
                    <input value={formData.unit} onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))} />
                  </div>

                  <div className="options-form-field">
                    <label>Порядок</label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value, 10) || 0 }))}
                    />
                  </div>

                  {formData.option_type === 'squad' && (
                    <div className="options-form-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Squad UUID</label>
                      {squads.length > 0 ? (
                        <select
                          value={formData.squad_uuid || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, squad_uuid: e.target.value }))}
                        >
                          <option value="">— выбрать —</option>
                          {squads.map(s => (
                            <option key={s.uuid || s.id} value={s.uuid || ''}>
                              {s.name ? `${s.name} (${s.uuid})` : (s.uuid || s.id)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={formData.squad_uuid || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, squad_uuid: e.target.value }))}
                          placeholder="UUID сквада"
                        />
                      )}
                    </div>
                  )}

                  <div className="options-form-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Описание</label>
                    <textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
                  </div>

                  <div className="options-form-field">
                    <label>Цена UAH</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_uah}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_uah: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="options-form-field">
                    <label>Цена RUB</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_rub}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_rub: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="options-form-field">
                    <label>Цена USD</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price_usd}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_usd: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>

                  <div className="options-form-field">
                    <label>Активна</label>
                    <select value={formData.is_active ? '1' : '0'} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === '1' }))}>
                      <option value="1">Да</option>
                      <option value="0">Нет</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="options-modal-footer">
                <button type="button" className="btn-secondary" onClick={() => !saving && setShowModal(false)} disabled={saving}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

